import React, {createContext, forwardRef, useContext, useEffect, useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {Box, Spinner} from '@chakra-ui/react3'

const LoadingContext = createContext(null)

/**
 * Root container for the loading indicator system with aria-busy state management.
 * This component wraps content that may be in a loading state and manages the aria-busy
 * attribute for accessibility. Must be the outermost component in the loading hierarchy.
 *
 * @component
 * @param {Object} props - Component properties.
 * @param {React.ReactNode} props.children - Child components (must include Status and Content).
 * @returns {JSX.Element} Rendered root container with aria-busy state management.
 *
 * @example
 * <Loading.Root>
 *   <Loading.Status>
 *     <Loading.Spinner isLoading={true} />
 *   </Loading.Status>
 *   <Loading.Content>
 *     <img src="..." alt="..." />
 *   </Loading.Content>
 * </Loading.Root>
 */
const LoadingRoot = forwardRef(({children, ...rest}, ref) => {
    const parentCtx = useContext(LoadingContext)
    if (parentCtx && parentCtx.parentType !== 'content') {
        throw new Error(
            `Loading.Root cannot be nested inside Loading.${parentCtx.componentName}. It must be inside Loading.Content.`
        )
    }

    const [internalLoading, setInternalLoading] = useState(false)
    const ownCtx = useMemo(
        () => ({
            isLoading: internalLoading,
            setLoading: setInternalLoading,
            parentType: 'root',
            componentName: 'Root'
        }),
        [internalLoading]
    )
    return (
        <LoadingContext.Provider value={ownCtx}>
            <Box aria-busy={internalLoading} position="relative" ref={ref} {...rest}>
                {children}
            </Box>
        </LoadingContext.Provider>
    )
})

if (process.env.NODE_ENV !== 'production') {
    LoadingRoot.displayName = 'LoadingRoot'
}

LoadingRoot.propTypes = {
    children: PropTypes.node
}

/**
 * Container for the main content that becomes inert during loading.
 * This component wraps the content that should become non-interactive (inert) when
 * loading is in progress. The inert attribute prevents keyboard navigation and
 * screen reader interaction with the content during loading.
 *
 * Must be a direct child of Loading.Root.
 *
 * @component
 * @param {Object} props - Component properties.
 * @param {React.ReactNode} props.children - The content to display (images, forms, etc.).
 * @returns {JSX.Element} Rendered content container that becomes inert during loading.
 */
const LoadingContent = forwardRef(({children, ...rest}, ref) => {
    const parentCtx = useContext(LoadingContext)
    if (!parentCtx) {
        throw new Error('Loading.Content must be used within a Loading.Root')
    }
    if (parentCtx.parentType !== 'root') {
        throw new Error(
            `Loading.Content cannot be nested inside Loading.${parentCtx.componentName}. It must be inside Loading.Root.`
        )
    }
    const {isLoading} = parentCtx

    const ownCtx = useMemo(
        () => ({
            parentType: 'content',
            componentName: 'Content'
        }),
        []
    )
    return (
        <LoadingContext.Provider value={ownCtx}>
            <Box ref={ref} {...rest} {...(isLoading && {inert: ''})}>
                {children}
            </Box>
        </LoadingContext.Provider>
    )
})

if (process.env.NODE_ENV !== 'production') {
    LoadingContent.displayName = 'LoadingContent'
}

LoadingContent.propTypes = {
    children: PropTypes.node
}

/**
 * ARIA live region container with role="status" for screen reader announcements.
 * This container persists in the DOM and provides an ARIA live region for
 * announcing loading state changes to screen readers. The container remains
 * on the page even when the spinner is not visible.
 *
 * Must be a direct child of Loading.Root.
 *
 * @component
 * @param {Object} props - Component properties.
 * @param {React.ReactNode} props.children - Loading spinner or status content.
 * @returns {JSX.Element} Rendered status container with role="status" attribute.
 */
const LoadingStatus = forwardRef(({children, ...rest}, ref) => {
    const parentCtx = useContext(LoadingContext)
    if (!parentCtx) {
        throw new Error('Loading.Status must be used within a Loading.Root')
    }
    if (parentCtx.parentType !== 'root') {
        throw new Error(
            `Loading.Status cannot be nested inside Loading.${parentCtx.componentName}. It must be inside Loading.Root.`
        )
    }
    const {setLoading} = parentCtx

    const ownCtx = useMemo(
        () => ({
            setLoading,
            parentType: 'status',
            componentName: 'Status'
        }),
        [setLoading]
    )

    return (
        <LoadingContext.Provider value={ownCtx}>
            <Box role="status" {...rest} ref={ref}>
                {children}
            </Box>
        </LoadingContext.Provider>
    )
})

if (process.env.NODE_ENV !== 'production') {
    LoadingStatus.displayName = 'LoadingStatus'
}

LoadingStatus.propTypes = {
    children: PropTypes.node
}

/**
 * Chakra UI Spinner component with accessibility features and automatic state management.
 * The spinner is centered within its container and uses semantic color tokens to
 * adapt to light/dark themes. Includes aria-label for screen reader accessibility.
 *
 * When isLoading is false, the spinner is removed from the DOM while the Status
 * (ARIA live region) persists. This follows WCAG guidelines for loading indicators.
 *
 * Must be a direct child of Loading.Status.
 *
 * @component
 * @param {Object} props - Component properties.
 * @param {boolean} [props.isLoading=true] - Whether the spinner should be visible.
 * @returns {JSX.Element|null} Rendered Chakra UI Spinner or null if not loading.
 */
const LoadingSpinner = forwardRef(({isLoading = true, ...rest}, ref) => {
    const ctx = useContext(LoadingContext)
    if (!ctx) {
        throw new Error('Loading.Spinner must be used within a Loading.Status')
    }
    if (ctx.parentType !== 'status') {
        throw new Error(
            `Loading.Spinner cannot be nested inside Loading.${ctx.componentName}. It must be inside Loading.Status.`
        )
    }
    const {setLoading} = ctx

    // update root with loading state
    useEffect(() => {
        setLoading(isLoading)
    }, [isLoading, setLoading])

    // handle removing the spinner when not loading instead of using the isLoading prop
    useEffect(() => {
        return () => {
            setLoading(false)
        }
    }, [setLoading])

    const {formatMessage} = useIntl()

    if (!isLoading) {
        return null
    }

    return (
        <Spinner
            // center in parent
            position="absolute"
            top="50%"
            left="50%"
            margin="calc(-0.5 * var(--spinner-size))" // can't use transform as it is overridden by the spinning animation
            // use semantic color
            color="text.primary"
            // aria
            aria-label={formatMessage({
                id: 'global.loading',
                defaultMessage: 'Loading...'
            })}
            // chakra composition
            ref={ref}
            {...rest}
        />
    )
})

if (process.env.NODE_ENV !== 'production') {
    LoadingSpinner.displayName = 'LoadingSpinner'
}

LoadingSpinner.propTypes = {
    isLoading: PropTypes.bool
}

/**
 * Loading indicator component system with full WCAG compliance.
 *
 * A compound component pattern for displaying loading states with comprehensive
 * accessibility features including ARIA live regions, aria-busy state management,
 * and inert content during loading.
 *
 * @namespace Loading
 *
 * @property {React.Component} Root - Root container with aria-busy state
 * @property {React.Component} Content - Content wrapper that becomes inert when loading
 * @property {React.Component} Status - ARIA live region (role="status")
 * @property {React.Component} Spinner - Chakra UI Spinner with accessibility features
 *
 * @example
 * // Basic usage with image loading
 * import Loading from '@app/leap/components/loading-container'
 *
 * function ImageWithLoader() {
 *   const [loading, setLoading] = useState(true)
 *
 *   return (
 *     <Loading.Root>
 *       <Loading.Status>
 *         <Loading.Spinner isLoading={loading} />
 *       </Loading.Status>
 *       <Loading.Content>
 *         <img
 *           src="..."
 *           alt="..."
 *           onLoad={() => setLoading(false)}
 *         />
 *       </Loading.Content>
 *     </Loading.Root>
 *   )
 * }
 *
 * @accessibility
 * - Uses aria-busy on root container (true during loading, false when complete)
 * - Provides persistent ARIA live region with role="status"
 * - Spinner has aria-label for screen reader announcements
 * - Content becomes inert during loading (prevents keyboard interaction)
 * - Spinner is removed while live region persists (WCAG compliant)
 * - Uses Chakra UI Spinner (compliant with no flashing >3 times/second)
 *
 * @design
 * - Color: Black for light theme, White for dark theme (via text.primary token)
 * - No visible label (aria-label only)
 * - Spinner is centered in container
 * - Responsive to theme changes
 */
const Loading = {
    Root: LoadingRoot,
    Content: LoadingContent,
    Status: LoadingStatus,
    Spinner: LoadingSpinner
}

export default Loading
